import { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const change = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Create a timeout promise
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Timeout")), 5000),
    );

    try {
      // Race Supabase call against the timeout
      const { data, error } = await Promise.race([
        supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password,
        }),
        timeout,
      ]);

      if (error) throw error;
      if (data.user) navigate("/dashboard");
    } catch (err) {
      console.error("Login attempt failed:", err.message);

      if (err.message === "Timeout") {
        setMsg({
          type: "error",
          text: "Connection timed out. Please try again.",
        });
      } else {
        setMsg({
          type: "error",
          text: getFriendlyError(err.message),
        });
      }
      setLoading(false);
    }
  };

  const getFriendlyError = (msg) => {
    switch (msg) {
      case "missing email or phone":
        return "Please enter your email and password.";
      case "Invalid login credentials":
        return "The email or password you entered is incorrect.";
      default:
        return msg; // Fallback to raw message
    }
  };

  // ----------- FORGOT PASSWORD LOGIC ----------- //

  const handleForgotPassword = async () => {
    if (!form.email) {
      setMsg({ type: "error", text: "Please enter your email address first." });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setMsg({ type: "error", text: error.message });
    } else {
      setMsg({
        type: "success",
        text: "Password reset link sent to your email!",
      });
    }
    setLoading(false);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  // -------------------- Render -------------------- //

  return (
    <Container maxWidth="xs" sx={{ py: 6, position: "relative" }}>
      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Login
      </Typography>

      {msg.text && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Box component="form" onSubmit={submit} noValidate>
        <TextField
          label="Email"
          name="email"
          type="email"
          fullWidth
          margin="normal"
          value={form.email}
          onChange={change}
          required
        />
        <TextField
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={form.password}
          onChange={change}
          required
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* FORGOT PASSWORD LINK */}
        <Box sx={{ textAlign: "right", mt: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleForgotPassword}
            sx={{ textTransform: "none" }}
          >
            Forgot password?
          </Button>
        </Box>

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 2 }}
          type="submit"
          disabled={loading}
        >
          {loading ? "Processing…" : "Sign In"}
        </Button>
      </Box>

      <Typography sx={{ mt: 3, textAlign: "center" }}>
        Don’t have an account? <Link to="/register">Register</Link>
      </Typography>
    </Container>
  );
}
